<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import axios from 'axios'
import dayjs from 'dayjs'
import { TriangleAlert } from 'lucide-vue-next'

import useNewTemplates from '@/views/autopilot/composables/newTemplates'

import CampaignPreview from '@/components/CampaignPreview.vue'
import { extractSurveyIdsFromMessageContent } from '@/components/ui/TextareaWithVars/utils'
import { isNumber } from '../utils'

import ReplaceMarketingFlowModal from '../Components/ReplaceMarketingFlowModal.vue'

import { usePrefetchSurveysForTextareaVars } from '@/composables/usePrefetchSurveysForTextareaVars'

import type { CustomVars } from '@/components/ui/TextareaWithVars/interfaces'
import type { Autopilot } from '@/types/global'
import type { LoyaltyReward } from '@/types/types'

const route = useRoute()
const router = useRouter()
const { user, permissions, permissionsByStores, stores } = useLocalStorage()
const { getStoresByPermission } = useStore()
const notification = useNotification()
const { formatNumber } = useNumber()
const { t } = useI18n()
const { snakeToCamel, camelToSnake } = useText()
const { fetchAllRewards } = useReward()

const isLoaded = ref(false)
const activity = ref([])
const table = ref({
  pagination: {
    currentPage: 1
  },
  columns: {
    customer: {
      label: t('global.autopilots.results.table.columns.customer')
    },
    date: {
      label: t('global.autopilots.results.table.columns.date')
    },
    store: {
      label: t('global.autopilots.results.table.columns.store')
    },
    converted: {
      label: t('global.autopilots.results.table.columns.converted')
    },
    convertedAt: {
      label: t('global.autopilots.results.table.columns.convertedAt')
    }
  },
  rows: []
})

const autopilot = ref({} as Autopilot)
const autopilotConversionDelayInDays = ref(0)
const autopilotRelaunchReccurrenceInDays = ref(0)
const autopilotConversionType = ref({
  sns: 1,
  visit: 2,
  qrcode: 3
})
const canSendSms = ref(false)
const currentTab = ref(1)
const currentTabString = computed({
  get: () => String(currentTab.value),
  set: (v: string) => {
    currentTab.value = Number(v)
  }
})
const message = ref('')
const messageMaxLength = ref(145)
const modal = ref({
  isOpen: false
})
const reviewLinkActive = ref(false)
const reviewLinkLength = ref(17)
const selected = ref({
  storeIds: []
})
const initialSelectedStoreIds = ref([])
const merchantStores = ref([])
const invalidCharacters = ref(false)
const isRewardModalOpen = ref(false)
const autopilotStores = ref([])
const rewards = ref<LoyaltyReward[]>([])
const selectedReward = ref<LoyaltyReward>()

const {
  surveys,
  isLoading: surveysLoading,
  loadError: surveysError,
  fetchSurveys: fetchTextareaSurveys
} = usePrefetchSurveysForTextareaVars()

//only accessible to Dalbe and internal accounts
const displayRelaunchDelay = computed(() => {
  return autopilot.value.trigger === 'LoyaltyRewardAvailable' || autopilot.value.trigger === 'loyaltyRewardClaimed'
})

const showAutopilotsEditButton = computed(() => {
  return permissions.value['autopilot.update']
})
const conversionRate = computed(() => {
  if (autopilot.value.countMessages === null || autopilot.value.countMessages === 0) {
    return '-'
  }
  return `${Number((autopilot.value.countConversions / autopilot.value.countMessages) * 100).toFixed(2)}%`
})
const title = computed(() => {
  return `global.autopilots.${autopilot.value.trigger}.name`
})
const tabs = computed(() => {
  return [
    {
      id: 1,
      name: t('pages.autopilot.autopilotDetailPage.tabs.message')
    },
    {
      id: 2,
      name: t('pages.autopilot.autopilotDetailPage.tabs.results')
    }
  ]
})

const conversionType = computed(() => {
  if (reviewLinkActive.value) {
    return autopilotConversionType.value.sns
  }
  if (autopilot.value.hasQrcode) {
    return autopilotConversionType.value.qrcode
  }
  return autopilotConversionType.value.visit
})

const conditions = computed<{ [key: string]: number }>(() => {
  const conditionsObj: { [key: string]: number } = {}
  for (const condition of autopilot.value?.conditions ?? []) {
    conditionsObj[snakeToCamel(condition.slug)] = condition.value
  }
  return conditionsObj
})

const storesWithoutReviewLink = computed(() => {
  return merchantStores.value.filter(
    (store) => isStoreDisabledBecauseReviewLink(store.id) && isStoreAvailableForUser(store.id)
  )
})

const getStoreClass = (storeId) => {
  return {
    'border-yellow-600 text-yellow-600': isStoreDisabledBecauseReviewLink(storeId) && isStoreAvailableForUser(storeId),
    'border-border text-foreground-disabled': isStoreDisabledBecauseUnavailable(storeId),
    'text-foreground': !isStoreDisabled(storeId)
  }
}

const changePage = (page) => {
  table.value.pagination.currentPage = page
  fetchActivity()
}
const fetchCanSendSms = async () => {
  try {
    const { data } = await axios.get(`merchants/${user.value.merchant.id}/can-send-sms`)
    canSendSms.value = data.data.canSendSms
  } catch {
    canSendSms.value = false
  }
}
const fetchData = async () => {
  await requestAllMerchantStores()
  await Promise.all([requestAutopilot(), fetchActivity()])
    .then(() => {
      getStoresByPermission('autopilot.read').forEach((store) => {
        autopilotStores.value.push({
          id: store.id,
          name: store.name
        })
      })
      if (autopilot.value.stores?.length === 0) {
        autopilotStores.value.forEach((store) => {
          if (!isStoreDisabledBecauseReviewLink(store.id)) {
            selected.value.storeIds.push(store.id)
          }
        })
      }
    })
    .finally(() => {
      if (autopilot.value.trigger !== 'LoyaltyRewardAvailable' && autopilot.value.trigger !== 'CustomerBirthday') {
        isLoaded.value = true
      }
    })
}
const requestAllMerchantStores = async () => {
  const [{ data: storesData }, { data: permissionsData }] = await Promise.all([
    axios.get('/stores'),
    axios.get('users/permissions')
  ])

  stores.value = storesData.data
  permissionsByStores.value = permissionsData.data

  merchantStores.value = []
  stores.value.forEach((store) => {
    merchantStores.value.push({
      id: store.id,
      name: store.name
    })
  })
}
const requestAutopilot = async () => {
  const { data } = await axios.get(`/autopilots/${route.params.autopilotId}`)
  autopilot.value = data.data
  autopilotConversionDelayInDays.value = autopilot.value.conversionDelayInHours / 24
  message.value = autopilot.value.messageContent
  reviewLinkActive.value = autopilot.value?.conversionType?.id === autopilotConversionType.value.sns
  autopilotRelaunchReccurrenceInDays.value = conditions.value.lastAutopilot

  // Filter stores to only include active stores
  autopilot.value.stores
    .filter((store) => store.isActive)
    .forEach((store) => {
      selected.value.storeIds.push(store.id)
      initialSelectedStoreIds.value.push(store.id)
    })
}

const shouldShowNotificationModal = ref(false)
const isReplaceMarketingFlowModalOpen = ref(false)

const { isCustomerBirthdayUsed, isCustomerVipUsed, isCustomerAtRiskUsed } = useNewTemplates()

const fetchActivity = async () => {
  const { data } = await axios.get(`/autopilots/${route.params.autopilotId}/activity`, {
    params: {
      pageSize: 10,
      page: table.value.pagination.currentPage
    }
  })
  activity.value = data.data
  const rows = []
  activity.value.forEach((row) => {
    rows.push({
      id: row.customerMerchant.id,
      data: {
        customer: {
          id: row.customerMerchant.id,
          phone: row.phone,
          name: row.customerMerchant.firstName,
          gender: row.customerMerchant.gender,
          hasOptin: row.customerMerchant.hasOptin,
          countryFlag: row.customerMerchant.countryFlag
        },
        date: {
          date: row.sentAt !== null ? new Date(row.sentAt).toLocaleDateString('fr-FR') : '-',
          time: row.sentAt !== null ? new Date(row.sentAt).toLocaleTimeString('fr-FR') : ''
        },
        store: row.store,
        converted: {
          icon: row.isConverted ? 'HeroCheckCircleIconSolid' : 'HeroXCircleIconSolid',
          color: row.isConverted ? 'green' : 'red'
        },
        convertedAt: {
          date: row.convertedAt !== null ? new Date(row.convertedAt).toLocaleDateString('fr-FR') : '-',
          time: row.convertedAt !== null ? new Date(row.convertedAt).toLocaleTimeString('fr-FR') : ''
        }
      }
    })
  })
  table.value.rows = rows
  table.value.pagination = {
    currentPage: data.meta.current_page,
    lastPage: data.meta.last_page,
    total: data.meta.total
  }
}
const toggleActive = () => {
  if (autopilot.value.trigger === 'CustomerNew' && !autopilot.value.isActive) {
    if (!shouldShowNotificationModal.value) {
      shouldShowNotificationModal.value = true
      return
    }
    shouldShowNotificationModal.value = false
  }

  if (!autopilot.value.isActive) {
    if (autopilot.value.trigger === 'CustomerBirthday') {
      if (isCustomerBirthdayUsed.value) {
        isReplaceMarketingFlowModalOpen.value = true
        return
      }
    }

    if (autopilot.value.trigger === 'CustomerVip') {
      if (isCustomerVipUsed.value) {
        isReplaceMarketingFlowModalOpen.value = true
        return
      }
    }

    if (autopilot.value.trigger === 'CustomerAtRisk') {
      if (isCustomerAtRiskUsed.value) {
        isReplaceMarketingFlowModalOpen.value = true
        return
      }
    }
  }

  autopilot.value.isActive = !autopilot.value.isActive
}

const replaceLegacy = () => {
  autopilot.value.isActive = !autopilot.value.isActive
  isReplaceMarketingFlowModalOpen.value = false
}

const unavailableStoresInitialySelected = computed(() =>
  initialSelectedStoreIds.value.filter(
    (id) => unavailableStores.value.filter((unavailable) => id === unavailable.id).length === 1
  )
)
const areAllStoresSelected = ref(false)
const setAreAllStoresSelected = () => {
  areAllStoresSelected.value =
    selected.value.storeIds.length - unavailableStoresInitialySelected.value.length ===
    autopilotStores.value.filter((store) => !isStoreDisabled(store.id)).length
}
const toggleAllStores = () => {
  if (areAllStoresSelected.value) {
    selected.value.storeIds = []
    areAllStoresSelected.value = false
  } else {
    selected.value.storeIds = autopilotStores.value.filter((store) => !isStoreDisabled(store.id)).map((x) => x.id)
    areAllStoresSelected.value = true
  }

  handleMessageLengthCalculation()
}
const toggleStore = (checked: boolean, storeId: number) => {
  if (checked) {
    selected.value.storeIds.push(storeId)
  } else {
    selected.value.storeIds = selected.value.storeIds.filter((id) => id !== storeId)
  }

  setAreAllStoresSelected()

  handleMessageLengthCalculation()
}

const save = () => {
  if (!canSendSms.value) {
    return notification.create('error', {
      text: t('pages.autopilot.autopilotsDetailPage.modal.noSmsPack.text'),
      timeout: 5000
    })
  }

  delete autopilot.value.stores
  const params = {
    ...autopilot.value,
    storeIds:
      selected.value.storeIds.length === merchantStores.value.length
        ? null
        : selected.value.storeIds.filter((storeId) => !isStoreDisabledBecauseReviewLink(storeId)),
    messageContent: message.value,
    conditions: camelToSnake(conditions.value),
    autopilotConversionTypeId: conversionType.value,
    hasQrcode: autopilot.value.hasQrcode,
    rewardId: selectedReward.value?.id ?? null
  }
  axios
    .put(`/autopilots/${autopilot.value.id}`, {
      ...params
    })
    .then(() => {
      modal.value = {
        isOpen: true,
        title: t('pages.autopilot.autopilotsDetailPage.modal.saveComplete.title'),
        text: t('pages.autopilot.autopilotsDetailPage.modal.saveComplete.text'),
        linkText: t('pages.autopilot.autopilotsDetailPage.modal.saveComplete.linkText'),
        icon: 'HeroCheckCircleIconSolid',
        to: 'AutopilotList'
      }
    })
    .catch((e) => {
      notification.create('error', {
        text: t(e.response.data?.message, {
          linkLength: reviewLinkLength.value
        }),
        timeout: 5000
      })
    })
}

const verifyDisableStores = () => {
  const selectedStores = []
  selected.value.storeIds.forEach((storeIds) => {
    if (!isStoreDisabledBecauseReviewLink(storeIds)) {
      selectedStores.push(storeIds)
    }
  })
  selected.value.storeIds = selectedStores
}
const unavailableStores = computed(() => {
  return merchantStores.value.filter((store) => !isStoreAvailableForUser(store.id))
})
const isStoreAvailableForUser = (storeId) => {
  return autopilotStores.value.filter((store) => store.id === storeId).length === 1
}
const isStoreDisabledBecauseUnavailable = (storeId) => {
  return !isStoreAvailableForUser(storeId)
}
const isStoreDisabledBecauseReviewLink = (storeId) => {
  if (!reviewLinkActive.value) {
    return false
  }
  return !stores.value.find((store) => store.id === storeId)?.googlePlaceId
}
const isStoreDisabled = (storeId) => {
  return isStoreDisabledBecauseUnavailable(storeId) || isStoreDisabledBecauseReviewLink(storeId)
}

watch(autopilotConversionDelayInDays, () => {
  autopilot.value.conversionDelayInHours = autopilotConversionDelayInDays.value * 24
})

const handleRewardModal = (isOpen: boolean) => {
  isRewardModalOpen.value = isOpen
}

const longestStoreName = computed(() => {
  return stores.value
    .filter((store) => selected.value.storeIds.includes(store.id))
    .reduce((longest, current) => (current.name.length > longest.name.length ? current : longest), {
      name: ''
    }).name
})

watch(message, () => {
  if (!message.value.includes('{{ reviewLink }}')) {
    reviewLinkActive.value = false
  }
  if (!message.value.includes('{{ qrcodeLink }}')) {
    autopilot.value.hasQrcode = false
  }

  if (message.value.includes('{{ reviewLink }}') && autopilot.value.hasQrcode === false) {
    reviewLinkActive.value = true
  }
  if (message.value.includes('{{ qrcodeLink }}') && reviewLinkActive.value === false) {
    autopilot.value.hasQrcode = true
  }
})

const customVariables = computed<CustomVars[]>(() => {
  const variables = [
    {
      type: 'variable',
      code: 'store',
      label: t('global.customVars.store.name'),
      icon: 'IconoirShop',
      length: longestStoreName.value.length,
      example: longestStoreName.value
    },
    {
      type: 'variable',
      code: 'points',
      label: t('global.customVars.points.name'),
      icon: 'HeroStarIconSolid',
      disabledMessage: t('global.customVars.points.disabledMessage'),
      length: autopilot.value?.bonusPoints ? autopilot.value.bonusPoints.toString()?.length : 0,
      example: autopilot.value?.bonusPoints ? autopilot.value.bonusPoints.toString() : '',
      disabled: autopilot.value?.bonusPoints === 0
    },
    {
      type: 'webapp',
      code: 'qrcodeLink',
      label: t('global.customVars.qrcodeLink.name'),
      icon: 'HeroQrCodeIconSolid',
      length: variablesLength.value?.qrcodeLink ?? 16,
      example: 'pngo.fr/qrcode',
      disabled: !autopilot.value.hasQrcode && reviewLinkActive.value === true
    },
    {
      type: 'link',
      code: 'reviewLink',
      label: t('global.customVars.reviewLink.name'),
      icon: 'HeroGlobeAltIconSolid',
      length: variablesLength.value?.reviewLink ?? 14,
      example: 'pngo.fr/review',
      disabled: !reviewLinkActive.value && autopilot.value.hasQrcode === true
    },
    {
      type: 'webapp',
      code: 'customerApp',
      label: t('global.customVars.customerApp.rewards.name'),
      icon: 'HeroGlobeAltIconSolid',
      length: variablesLength.value?.customerApp ?? 16,
      example: 'pngo.fr/webapp'
    },
    {
      type: 'webapp',
      code: 'customerApp(customer-profile)',
      label: t('global.customVars.customerApp.customerProfile.name'),
      icon: 'HeroGlobeAltIconSolid',
      length: variablesLength.value?.customerApp ?? 16,
      example: 'pngo.fr/webapp'
    },
    {
      type: 'webapp',
      code: 'customerApp(wallet)',
      label: t('global.customVars.customerApp.wallet.name'),
      icon: 'HeroGlobeAltIconSolid',
      length: variablesLength.value?.customerApp ?? 16,
      example: 'pngo.fr/webapp'
    },
    {
      type: 'webapp',
      code: 'customerApp(survey)',
      label: t('global.customVars.customerApp.survey.name'),
      icon: 'HeroGlobeAltIconSolid',
      length: variablesLength.value?.customerApp ?? 16,
      example: 'pngo.fr/webapp',
      customValueType: 'pageId',
      customValueModel: 'survey'
    }
  ]

  if (autopilot.value.trigger === 'CustomerBirthday') {
    variables.push({
      type: 'variable',
      code: 'reward',
      label: t('global.customVars.reward.name'),
      icon: 'HeroGiftIconSolid',
      length: selectedReward.value ? selectedReward.value.name.length : 0,
      example: selectedReward.value?.name,
      disabled: !selectedReward.value?.name
    })
    variables.push({
      type: 'webapp',
      code: 'rewardLink',
      label: t('global.customVars.rewardLink.name'),
      icon: 'HeroGiftIconSolid',
      length: variablesLength.value?.rewardLink ?? 16,
      example: 'pngo.fr/reward',
      disabled: !selectedReward.value?.name
    })

    variables.push({
      type: 'variable',
      code: 'voucher',
      label: t('global.customVars.voucher.name'),
      icon: 'HeroReceiptPercentIconSolid',
      length: variablesLength.value?.voucher ?? 10,
      example: 'XXXXXXXXXX',
      disabled: !selectedReward.value?.rewardType.isVoucher
    })
  }

  if (autopilot.value.trigger === 'LoyaltyRewardAvailable') {
    const trigger = autopilot.value.triggers?.find((trigger) => trigger.slug === 'loyalty_reward_available')
    const loyaltyReward = trigger?.optionValue
      ? rewards.value[trigger?.optionValue]
      : rewards.value[Number(trigger?.defaultOption)]

    if (loyaltyReward) {
      variables.push({
        code: 'reward',
        label: t('global.customVars.reward.name'),
        icon: 'HeroGiftIconSolid',
        length: loyaltyReward.name.length,
        example: loyaltyReward.name
      })
    }
  }
  return variables as CustomVars[]
})

const messageSurvey = computed(() => {
  if (!surveys.value?.length) return null

  const surveyIds = extractSurveyIdsFromMessageContent(message.value ?? '')
  const firstId = surveyIds[0]
  if (firstId == null || Number.isNaN(firstId)) return null

  return surveys.value?.find((survey) => survey.id === firstId) ?? null
})

const qrCodeText = computed(() => {
  const placeholder = '{{ qrcodeLink }}'
  return ` ${placeholder}`
})

const rewardLinkText = computed(() => {
  const placeholder = '{{ rewardLink }}'
  return ` ${placeholder}`
})

const reviewLinkText = computed(() => {
  const placeholder = '{{ reviewLink }}'
  return ` ${placeholder}`
})

const selectReward = (rewardId: number) => {
  const reward = rewards.value.find((reward) => reward.id === rewardId)
  updateMessageText(rewardLinkText.value, true)
  selectedReward.value = reward
  handleRewardModal(false)
  checkMessageContent()
}

const clearReward = () => {
  updateMessageText(rewardLinkText.value, false)
  selectedReward.value = null
  checkMessageContent()
}

const toggleReviewLink = () => {
  reviewLinkActive.value = !reviewLinkActive.value
  autopilot.value.hasQrcode = false
  verifyDisableStores()

  if (reviewLinkActive.value) {
    updateMessageText(reviewLinkText.value, true)
    updateMessageText(qrCodeText.value, false)
  } else {
    updateMessageText(reviewLinkText.value, false)
  }
  checkMessageContent()
}

const toggleQrCode = () => {
  autopilot.value.hasQrcode = !autopilot.value.hasQrcode
  reviewLinkActive.value = false

  if (autopilot.value.hasQrcode) {
    updateMessageText(qrCodeText.value, true)
    updateMessageText(reviewLinkText.value, false)
  } else {
    updateMessageText(qrCodeText.value, false)
  }
  checkMessageContent()
}

const updateMessageText = (textToAdd: string, shouldAdd: boolean) => {
  if (shouldAdd && !message.value.includes(textToAdd)) {
    message.value += textToAdd
  } else if (!shouldAdd) {
    message.value = message.value.replace(textToAdd, '')
  }
}
const checkMessageContent = () => {
  invalidCharacters.value = /[^\p{L}\p{N}\p{P}\p{Z}\p{M}^$€<>=+¨~−\n]/gu.test(message.value)
  handleMessageLengthCalculation()
}

const fetchRewards = async () => {
  const data = await fetchAllRewards()
  rewards.value = data
}

const isCheckingMessageLength = ref(false)
const messageLength = ref(0)
const hasMessageLengthError = ref(false)
const hasMessageContentError = ref(false)
const variablesLength = ref<Record<string, number>>({})

const handleMessageLengthCalculation = () => {
  isCheckingMessageLength.value = true
  messageLength.value = null

  getMessageLength()
}

const getMessageLength = useDebounceFn(async () => {
  if (!message.value?.length) {
    messageLength.value = 0
    isCheckingMessageLength.value = false
  } else {
    try {
      const { data } = await axios.get('/autopilots/message-length', {
        params: {
          message: message.value,
          storeIds:
            stores.value?.filter((store) => selected.value.storeIds.includes(store.id))?.map((store) => store.id) || [],
          rewardId: selectedReward.value?.id,
          points: isNumber(autopilot.value?.bonusPoints) ? Number(autopilot.value?.bonusPoints) : null
        }
      })
      messageLength.value = data.data.messageLength

      hasMessageLengthError.value = messageLength.value > messageMaxLength.value

      isCheckingMessageLength.value = false
    } catch {
      messageLength.value = 0
      isCheckingMessageLength.value = false
      notification.create('error', {
        text: t('pages.campaign.campaignCreatePage.notifications.test.messageLengthCalculationError')
      })
    }
  }
}, 500)

const getVariablesLength = async () => {
  try {
    const { data } = await axios.get('autopilots/variables-length', {
      params: {
        storeIds:
          stores.value?.filter((store) => selected.value.storeIds.includes(store.id))?.map((store) => store.id) || [],
        rewardId: selectedReward.value?.id,
        points: isNumber(autopilot.value?.bonusPoints) ? Number(autopilot.value?.bonusPoints) : null
      }
    })
    variablesLength.value = data.data
  } catch {
    notification.create('error', {
      text: t('pages.campaign.campaignCreatePage.notifications.test.messageLengthCalculationError')
    })
  }
}

onMounted(async () => {
  await Promise.all([fetchData(), fetchTextareaSurveys()])
  await fetchCanSendSms()
  await fetchRewards()

  selectedReward.value = rewards.value.find((reward) => reward.id === autopilot.value.reward?.id)
  if (selectedReward.value) {
    updateMessageText(rewardLinkText.value, true)
    checkMessageContent()
  }

  await getVariablesLength()
  handleMessageLengthCalculation()

  setAreAllStoresSelected()
  isLoaded.value = true
})
</script>

<template>
  <div v-if="isLoaded">
    <TheDetailHeader
      :title="t(title)"
      :parent="{ label: t('pages.autopilot.autopilotsPage.title'), to: { name: 'AutopilotList' } }"
    >
      <template #actions>
        <div class="flex items-center gap-2">
          <button
            v-if="showAutopilotsEditButton"
            type="button"
            :class="[
              'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-0 focus:ring-offset-2',
              autopilot.isActive ? 'bg-green-500' : 'bg-red-300'
            ]"
            aria-pressed="false"
            @click="toggleActive"
          >
            <span class="sr-only">Activate</span>
            <span
              aria-hidden="true"
              :class="[
                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out',
                autopilot.isActive ? 'translate-x-5' : 'translate-x-0'
              ]"
            />
          </button>
          <span class="text-sm font-medium" :class="autopilot.isActive ? 'text-green-500' : 'text-red-500'">
            {{ autopilot.isActive ? t('global.words.enabled') : t('global.words.disabled') }}
          </span>
        </div>
        <div class="flex items-center space-x-4">
          <Button variant="outline" @click="router.push({ name: 'AutopilotList', query: { tab: 'legacy' } })">
            {{ t('global.words.cancel') }}
          </Button>

          <Button
            v-if="showAutopilotsEditButton"
            variant="default-primary"
            :disabled="invalidCharacters || hasMessageContentError || hasMessageLengthError"
            @click="save"
          >
            {{ t('global.words.save') }}
          </Button>
        </div>
      </template>
    </TheDetailHeader>

    <div
      class="hidden px-6 sm:px-12 lg:grid lg:grid-cols-3 lg:gap-4"
      :class="displayRelaunchDelay ? 'lg:grid-cols-4' : 'lg:grid-cols-3'"
    >
      <div class="relative flex items-center space-x-3 rounded-lg border bg-white px-6 py-5">
        <span class="inline-flex rounded-lg bg-primary/5 p-3 text-primary ring-4 ring-white">
          <BaseIcon name="HeroBoltIconOutline" class="h-6 w-6" />
        </span>
        <div class="min-w-0 flex-1">
          <span class="absolute inset-0" aria-hidden="true" />
          <p class="text-sm font-semibold text-foreground">
            {{ t('pages.autopilot.autopilotDetailPage.summary.trigger') }}
          </p>
          <p class="mt-0.5 text-sm text-muted-foreground">
            {{ t('global.autopilots.' + autopilot.trigger + '.trigger') }}
          </p>
        </div>
      </div>
      <div class="relative flex items-center space-x-3 rounded-lg border bg-white px-6 py-5">
        <span class="inline-flex rounded-lg bg-primary/5 p-3 text-primary ring-4 ring-white">
          <BaseIcon name="HeroChatBubbleLeftEllipsisIconOutline" class="h-6 w-6" />
        </span>
        <div class="min-w-0 flex-1">
          <span class="absolute inset-0" aria-hidden="true" />
          <p class="text-sm font-semibold text-foreground">
            {{ t('pages.autopilot.autopilotDetailPage.summary.message') }}
          </p>
          <p class="mt-0.5 break-words text-sm text-muted-foreground">
            {{ autopilot.messageContent }}
          </p>
        </div>
      </div>
      <div
        v-if="displayRelaunchDelay"
        class="relative flex items-center space-x-3 rounded-lg border bg-white px-6 py-5"
      >
        <span class="inline-flex rounded-lg bg-primary/5 p-3 text-primary ring-4 ring-white">
          <BaseIcon name="HeroArrowPathIconSolid" class="h-6 w-6" />
        </span>
        <div class="min-w-0 flex-1">
          <span class="absolute inset-0" aria-hidden="true" />
          <p class="text-sm font-semibold text-foreground">
            {{ t('pages.autopilot.autopilotDetailPage.summary.relaunch') }}
          </p>
          <p class="mt-0.5 break-words text-sm text-muted-foreground">
            {{
              t('pages.autopilot.autopilotsDetailPage.blocks.relaunch.reccurence', {
                delay: autopilotRelaunchReccurrenceInDays
              })
            }}
          </p>
        </div>
      </div>
      <div class="relative flex items-center space-x-3 rounded-lg border bg-white px-6 py-5">
        <div>
          <span class="inline-flex rounded-lg bg-primary/5 p-3 text-primary ring-4 ring-white">
            <BaseIcon name="HeroUsersIconOutline" class="h-6 w-6" />
          </span>
        </div>
        <div class="min-w-0 flex-1">
          <span class="absolute inset-0" aria-hidden="true" />
          <p class="text-sm font-semibold text-foreground">
            {{ t('pages.autopilot.autopilotDetailPage.summary.recipients') }}
          </p>
          <p class="mt-0.5 text-sm text-muted-foreground">
            <span v-if="autopilot.countStores === 0">{{
              t('pages.autopilot.autopilotsDetailPage.blocks.global.customersOfAllStores')
            }}</span>
            <span v-else>{{
              t('pages.autopilot.autopilotsDetailPage.blocks.global.customersOfSomeStores', {
                count: autopilot.countStores
              })
            }}</span>
          </p>
        </div>
      </div>
    </div>

    <div class="mt-6 px-6 sm:px-12">
      <Tabs v-model="currentTabString">
        <TabsList variant="underline">
          <TabsTrigger v-for="tab in tabs" :key="tab.id" variant="underline" :value="String(tab.id)">
            {{ tab.name }}
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>

    <div v-if="currentTab === 1" class="space-y-8 px-6 pb-6 sm:px-12 sm:pb-12">
      <div class="mt-8 lg:grid lg:grid-cols-3 lg:gap-5">
        <div class="md:col-span-2">
          <div class="border bg-white sm:rounded-md">
            <div class="border-b p-5">
              <h3 class="font-semibold leading-6 text-foreground">
                {{ t('pages.autopilot.autopilotDetailPage.message.title') }}
              </h3>
            </div>

            <div class="mt-6 w-full px-5 pb-5">
              <TextareaWithVars
                v-model="message"
                label="about"
                :max-length="messageMaxLength"
                :i18n-strings="{
                  textareaLabel: t('pages.autopilot.autopilotDetailPage.message.sms'),
                  characters: t('global.words.characters'),
                  insertVar: t('global.customVars.insertLabel'),
                  errorEmojiInMessage: t('ui.error.strings.unauthorizedEmoji'),
                  customValue: {
                    placeholder: t('global.customVars.customValue.placeholder'),
                    create: t('global.customVars.customValue.add'),
                    update: t('global.customVars.customValue.modify'),
                    back: t('global.customVars.customValue.back')
                  }
                }"
                :custom-vars="customVariables"
                :sanitize-for-gsm="true"
                :required-store-ids="selected.storeIds"
                :surveys="surveys"
                :surveys-loading="surveysLoading"
                :surveys-error="surveysError"
                @has-error="hasMessageContentError = $event"
                @update:model-value="checkMessageContent"
                @update:example-value="autopilot.messageContent = $event"
              >
                <template #messageLength>
                  <span v-if="isCheckingMessageLength"> {{ t('global.words.loading') }}... </span>
                  <span v-else>
                    <span :class="{ 'text-red-600': messageLength > messageMaxLength }">
                      {{ messageLength }}/{{ messageMaxLength }} {{ t('global.words.characters') }}
                    </span>
                  </span>
                </template>
                <BaseTip
                  v-if="messageSurvey"
                  severity="warn"
                  class="my-5"
                  :text="
                    messageSurvey.endDate
                      ? t('global.survey.linkedMessageTipWithEndDate', {
                          startDate: dayjs(messageSurvey.launchDate * 1000).format('DD/MM/YYYY'),
                          endDate: dayjs(messageSurvey.endDate * 1000).format('DD/MM/YYYY')
                        })
                      : t('global.survey.linkedMessageTipFromStartDate', {
                          startDate: dayjs(messageSurvey.launchDate * 1000).format('DD/MM/YYYY')
                        })
                  "
                />
              </TextareaWithVars>
            </div>
          </div>
          <div class="mt-8 border bg-white sm:rounded-md">
            <div class="border-b p-5">
              <h3 class="font-semibold leading-6 text-foreground">
                {{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.title') }}
              </h3>
            </div>

            <div class="grid grid-cols-1 gap-x-4 gap-y-7 p-5 sm:grid-cols-6">
              <div v-if="autopilot.trigger === 'CustomerBirthday'" class="sm:col-span-6">
                <label for="reviewLink" class="block text-sm font-medium text-foreground">
                  {{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.reward.title') }}
                </label>
                <div class="mt-0.5 flex flex-col gap-3 text-sm">
                  <p class="mb-2 text-foreground-secondary sm:mb-0">
                    {{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.reward.subtitle') }}
                  </p>
                  <div class="flex items-center justify-center rounded-lg border bg-muted">
                    <div v-if="!selectedReward" class="p-7">
                      <Button variant="outline" @click="handleRewardModal(true)">
                        <BaseIcon name="HeroPlusIconOutline" class="h-5 w-5" />
                        <p>{{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.reward.add') }}</p>
                      </Button>
                    </div>
                    <div v-else class="w-full p-4">
                      <RewardItem
                        :reward="selectedReward"
                        :is-reward-selected="true"
                        :button-shown="['clear', 'change']"
                        @clear-reward="clearReward"
                        @change-reward="handleRewardModal(true)"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="sm:col-span-6">
                <label for="bonusPoints" class="block text-sm font-medium text-foreground">
                  {{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.bonusPoints.title') }}
                  <p class="mt-0.5 font-normal text-foreground-secondary">
                    {{
                      t(
                        `pages.autopilot.autopilotDetailPage.scenarioOptions.bonusPoints.description.${
                          reviewLinkActive ? 'withReviewLink' : 'nextVisit'
                        }`
                      )
                    }}
                  </p>
                </label>
                <div class="mt-3 w-full md:w-1/2">
                  <AppFormInput
                    id="bonusPoints"
                    v-model="autopilot.bonusPoints"
                    @input="handleMessageLengthCalculation"
                  >
                    <template #icon-right>
                      <p class="text-sm font-normal text-foreground-secondary">
                        {{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.reward.points') }}
                      </p>
                    </template>
                  </AppFormInput>
                </div>
              </div>
              <div class="sm:col-span-6">
                <label for="reviewLink" class="block text-sm font-medium text-foreground">
                  {{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.reviewLink.title') }}
                </label>
                <div class="flex flex-col justify-between text-sm sm:flex-row sm:items-center">
                  <p class="mb-2 mt-0.5 text-foreground-secondary">
                    {{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.reviewLink.description') }}
                  </p>
                  <BaseSwitch
                    id="reviewLink"
                    :model-value="reviewLinkActive"
                    :label="
                      reviewLinkActive
                        ? t('pages.autopilot.autopilotDetailPage.scenarioOptions.reviewLink.switchActivated')
                        : t('pages.autopilot.autopilotDetailPage.scenarioOptions.reviewLink.switchDeactivated')
                    "
                    font-color-gradient="600"
                    :font-color="reviewLinkActive ? 'green' : 'red'"
                    is-reversed
                    @click="toggleReviewLink"
                  />
                </div>

                <BaseTip
                  v-if="storesWithoutReviewLink.length > 0"
                  severity="warn"
                  class="my-5"
                  :text="
                    t('pages.autopilot.autopilotDetailPage.scenarioOptions.reviewLink.tip', {
                      count: storesWithoutReviewLink.length
                    })
                  "
                />
              </div>

              <div class="sm:col-span-6">
                <label for="uniqueQrCode" class="block text-sm font-medium text-foreground">
                  {{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.qrCodeBlock.title') }}
                </label>
                <div class="flex flex-col justify-between text-sm sm:flex-row sm:items-center">
                  <p class="mt-0.5 text-foreground-secondary">
                    {{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.qrCodeBlock.description') }}
                  </p>
                  <BaseSwitch
                    id="uniqueQrCode"
                    :model-value="autopilot.hasQrcode"
                    :label="
                      autopilot.hasQrcode
                        ? t('pages.autopilot.autopilotDetailPage.scenarioOptions.qrCodeBlock.switchActivated')
                        : t('pages.autopilot.autopilotDetailPage.scenarioOptions.qrCodeBlock.switchDeactivated')
                    "
                    font-color-gradient="600"
                    :font-color="autopilot.hasQrcode ? 'green' : 'red'"
                    is-reversed
                    @click="toggleQrCode"
                  />
                </div>
              </div>

              <div class="sm:col-span-6">
                <label for="conversionDaysCount" class="block text-sm font-medium text-foreground">
                  {{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.conversionDaysCount.title') }}
                  <p class="mt-0.5 text-sm font-normal text-foreground-secondary">
                    {{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.conversionDaysCount.description') }}
                  </p>
                </label>
                <div class="mt-1">
                  <input
                    id="conversionDaysCount"
                    v-model="autopilotConversionDelayInDays"
                    type="number"
                    name="conversionDaysCount"
                    class="w-full rounded-control border-input focus:border-primary focus:ring-0 sm:w-1/2 sm:text-sm"
                  />
                </div>
              </div>

              <div v-if="displayRelaunchDelay" class="sm:col-span-6">
                <label for="conditionLastAutopilot" class="block text-sm font-medium text-foreground">
                  {{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.lastAutopilotCondition.title') }}
                  <p class="mt-0.5 text-sm font-normal text-foreground-secondary">
                    {{ t('pages.autopilot.autopilotDetailPage.scenarioOptions.lastAutopilotCondition.description') }}
                  </p>
                </label>
                <div class="mt-1">
                  <input
                    id="conditionLastAutopilot"
                    v-model="conditions.lastAutopilot"
                    type="number"
                    name="conditionLastAutopilot"
                    class="rounded-control border-input focus:border-primary focus:ring-0 sm:text-sm"
                    @input="autopilotRelaunchReccurrenceInDays = conditions.lastAutopilot"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="mt-7 border bg-white sm:rounded-md">
            <div class="flex justify-between border-b p-5">
              <div>
                <h3 class="font-semibold leading-6 text-foreground">
                  {{ t('pages.autopilot.autopilotDetailPage.message.stores.title') }}
                </h3>
                <p class="mt-1 text-sm text-foreground-secondary">
                  {{ t('pages.autopilot.autopilotDetailPage.message.stores.description') }}
                </p>
              </div>
              <AppCheckboxField
                id="selectAllStores"
                :model-value="areAllStoresSelected"
                :label="t('pages.autopilot.autopilotDetailPage.message.stores.selectAll')"
                @update:model-value="toggleAllStores"
              />
            </div>

            <div class="mt-8 grid grid-cols-1 gap-4 px-8 pb-8 sm:grid-cols-2">
              <div
                v-for="store in merchantStores"
                :key="store.id"
                class="relative flex items-center justify-center space-x-3 rounded-lg border bg-white p-3"
                :class="isStoreDisabled(store.id) ? 'border-border' : 'border-border hover:border-border'"
              >
                <div class="flex min-w-0 flex-1 justify-between">
                  <div class="flex items-center">
                    <label
                      :for="store.id"
                      class="flex cursor-pointer items-center text-sm font-medium"
                      :class="getStoreClass(store.id)"
                    >
                      <BaseIcon
                        v-if="!isStoreAvailableForUser(store.id)"
                        name="HeroLockClosedIconSolid"
                        class="mr-2 h-4 w-4 shrink-0"
                      />
                      <TriangleAlert
                        v-else-if="isStoreDisabledBecauseReviewLink(store.id) && isStoreAvailableForUser(store.id)"
                        class="mr-2 h-4 w-4 shrink-0"
                      />
                      {{ store.name }}
                    </label>
                  </div>
                  <div>
                    <a
                      v-if="isStoreDisabledBecauseReviewLink(store.id) && isStoreAvailableForUser(store.id)"
                      :href="`/settings/stores/${store.id}`"
                      target="_blank"
                      class="flex items-center text-sm font-medium text-yellow-600"
                    >
                      {{ t('pages.autopilot.autopilotDetailPage.message.stores.update') }}
                      <BaseIcon name="HeroArrowLongRightIconSolid" class="ml-3 h-5 w-5 shrink-0 text-yellow-600" />
                    </a>
                    <Checkbox
                      v-else
                      :id="store.id"
                      :model-value="selected.storeIds.includes(store.id)"
                      :disabled="isStoreDisabled(store.id)"
                      @update:model-value="(checked) => toggleStore(!!checked, store.id)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-7 lg:mt-0">
          <div class="h-96 overflow-hidden border bg-white sm:rounded-lg">
            <div class="border-b p-5">
              <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
                <h3 class="font-semibold leading-6 text-foreground">
                  {{ t('pages.autopilot.autopilotDetailPage.message.preview.title') }}
                </h3>
              </div>
            </div>
            <div class="relative mx-auto h-72">
              <CampaignPreview
                :name="user.merchant.smsName"
                :message="autopilot.messageContent"
                :raw-message="autopilot.messageContent"
                :reward-name="selectedReward?.name"
                type="sms"
                class="h-full"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="currentTab === 2" class="mt-6 px-6 pb-6 sm:px-12 sm:pb-12">
      <BaseTip severity="info" class="my-6" :text="t('pages.autopilot.autopilotsPage.legacy')" />

      <div>
        <dl class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-4">
          <AppStatsCard :title="t('pages.autopilot.autopilotDetailPage.results.sent')">
            {{ formatNumber(autopilot.countMessages) }}
          </AppStatsCard>
          <AppStatsCard :title="t('pages.autopilot.autopilotDetailPage.results.converted')">
            {{ formatNumber(autopilot.countConversions) }}
          </AppStatsCard>
          <AppStatsCard :title="t('pages.autopilot.autopilotDetailPage.results.conversionRate')">
            {{ conversionRate }}
          </AppStatsCard>
          <AppStatsCard :title="t('pages.autopilot.autopilotDetailPage.results.usedReward')">
            {{ formatNumber(autopilot?.countUsedRewards) }}
          </AppStatsCard>
        </dl>
      </div>

      <div class="mt-8 flex flex-col">
        <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <AppDownloadCsvButton
              :path="`autopilots/${route.params.autopilotId}/downloadCsv`"
              :filename="`pongo_export_autopilot_${autopilot.trigger}.csv`"
            />
            <AppTable
              :columns="table.columns"
              :rows="table.rows"
              :pagination="table.pagination"
              @row-clicked="rowClicked"
              @update:change-page="changePage"
            >
              <template #td-customer="{ data }">
                <RowCustomer
                  :id="data.customer.id"
                  :country-flag="data.customer.countryFlag ?? null"
                  :has-optin="data.customer.hasOptin"
                  :name="data.customer.name"
                  :phone="data.customer.phone"
                />
              </template>
              <template #td-date="{ data }">
                <div>
                  <div class="text-sm font-medium text-foreground">{{ data.date.date }}</div>
                  <div v-if="data.date.time" class="text-sm text-muted-foreground">
                    {{ data.date.time }}
                  </div>
                </div>
              </template>
              <template #td-converted="{ data }">
                <BaseIcon :name="data.converted.icon" :class="`h-5 w-5 text-${data.converted.color}-500`" />
              </template>
              <template #td-convertedAt="{ data }">
                <div>
                  <div class="text-sm font-medium text-foreground">{{ data.convertedAt.date }}</div>
                  <div v-if="data.convertedAt.time" class="text-sm text-muted-foreground">
                    {{ data.convertedAt.time }}
                  </div>
                </div>
              </template>
            </AppTable>
          </div>
        </div>
      </div>
    </div>

    <BaseModal
      :is-open="modal.isOpen"
      :link-text="modal.linkText"
      :text-title="modal.title"
      :text="modal.text"
      :icon="modal.icon"
      icon-color="green"
      @click="router.push({ name: modal.to, query: { tab: 'legacy' } })"
    />

    <ReplaceMarketingFlowModal
      :is-open="isReplaceMarketingFlowModalOpen"
      @on-cancel="isReplaceMarketingFlowModalOpen = false"
      @on-confirm="replaceLegacy"
    />

    <RewardSelectionModal
      :is-open="isRewardModalOpen"
      :rewards="rewards"
      @close-modal="handleRewardModal(false)"
      @create-reward="router.push({ name: 'LoyaltyEdit', query: { openModal: 'true' } })"
      @select-reward="selectReward"
    />
  </div>
  <TheLoadingPage v-else />
</template>
